import { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { DonateModal } from "@/components/DonateModal";

const navLinks = [
  { label: "About", href: "#mission" },
  { label: "Classes", href: "#classes" },
  { label: "Sponsors", href: "#sponsors" },
  { label: "Partners", href: "#partners" },
  { label: "Pathways", href: "#programs" },
  { label: "Koda Suite", href: "#koda" },
  { label: "Team", href: "#team" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

interface NavbarProps {
  studentLink?: string;
  showBanner?: boolean;
  bannerText?: string;
  showDonate?: boolean;
}

export const Navbar = ({ studentLink, showBanner = false, bannerText = "", showDonate = false }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const navbarHeight = document.querySelector("header")?.offsetHeight ?? 64;
      const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight - 16;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <>
    <DonateModal isOpen={donateOpen} onClose={() => setDonateOpen(false)} />
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      {showBanner && (
        <div className="w-full bg-linear-to-r from-primary to-primary/80 text-white text-center py-2 lg:py-3 px-4 text-sm lg:text-base font-medium">
          <p>{bannerText}&nbsp;<a href={studentLink || "#"} target="_blank" className="font-semibold underline hover:opacity-90 transition-opacity whitespace-nowrap">Join now →</a></p>
        </div>
      )}
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Mobile/tablet hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="xl:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <div className="flex flex-col h-full">
                {/* Sheet header */}
                <div className="flex items-center gap-2.5 px-6 py-5 border-b border-border">
                  <div className="p-1.5 bg-primary rounded-lg">
                    <img src="64x64 Blue.png" width={20} height={20} alt="" aria-hidden="true" />
                  </div>
                  <span className="font-semibold text-foreground text-base">
                    Byte-Sized Python
                  </span>
                </div>

                {/* Sheet nav links */}
                <nav className="flex flex-col gap-1 px-3 py-4 flex-1" aria-label="Mobile navigation">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.label}>
                      <a
                        href={link.href}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(link.href);
                        }}
                        className="flex items-center px-3 py-3 text-base font-medium text-foreground hover:text-primary rounded-lg hover:bg-primary/5 transition-colors"
                      >
                        {link.label}
                      </a>
                    </SheetClose>
                  ))}
                </nav>

                {/* Sheet CTA */}
                <div className="px-6 py-6 border-t border-border">
                  <SheetClose asChild>
                    <Button
                      variant="hero"
                      className="w-full"
                      onClick={() => showDonate ? setDonateOpen(true) : window.open(studentLink || "#", "_blank")}
                    >
                      {showDonate ? "Donate Now" : "Join Now"}
                    </Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2.5"
          >
            <div className="p-1.5 bg-primary rounded-lg">
              <img src="64x64 Blue.png" width={20} height={20} alt="" aria-hidden="true" />
            </div>
            <span className="font-semibold text-foreground text-sm sm:text-base">
              Byte-Sized Python
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1" aria-label="Main navigation">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(link.href);
              }}
              className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-foreground/5"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden xl:flex items-center gap-3">
          <Button
            variant="hero"
            size="sm"
            onClick={() => showDonate ? setDonateOpen(true) : window.open(studentLink || "#", "_blank")}
          >
            {showDonate ? "Donate Now" : "Join Now"}
          </Button>
        </div>
      </div>
    </header>
    </>
  );
};
